import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Drawer,
  Fab,
  Grid,
  Hidden,
  IconButton,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Help as HelpIcon,
  Close as CloseIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  sidebarFab: {
    position: 'fixed',
    zIndex: theme.zIndex.speedDial,
  },
  drawer: {
    '& .MuiDrawer-paper': {
      width: '90vw',
    },
  },
}));

function ResponsiveSidebar(props) {
  const {
    children,
    fabProps = {},
    drawerProps = {},
    cardProps = {},
    gridProps = {},
    breakpoint = 'sm',
    fabPosition = {
      bottom: 16,
      right: 16,
    },
    drawerWidth = {
      mobile: '90vw',
      maxWidth: 400,
    },
    showCloseButton = true,
    anchor = 'right',
    className,
    // ...otherProps
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(breakpoint));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fabStyles = {
    ...fabPosition,
    [theme.breakpoints.up(breakpoint === 'sm' ? 'md' : 'lg')]: {
      display: 'none',
    },
  };

  const drawerStyles = {
    '& .MuiDrawer-paper': {
      width: drawerWidth.mobile,
      maxWidth: drawerWidth.maxWidth,
    },
  };

  const handleOpen = () => setSidebarOpen(true);
  const handleClose = () => setSidebarOpen(false);

  const renderContent = () => {
    if (typeof children === 'function') {
      return children({
        isMobile,
        isOpen: sidebarOpen,
        onClose: handleClose,
      });
    }
    return children;
  };

  const contentWithCloseButton = showCloseButton && isMobile ? (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 1,
        }}
      >
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      {renderContent()}
    </Box>
  ) : (
    renderContent()
  );

  return (
    <>
      {/* Sidebar para Desktop */}
      <Hidden {...{
        [`${breakpoint}Down`]: true,
      }}>
        <Grid
          item
          {...{
            md: 3,
            sm: 4,
            ...gridProps,
          }}
        >
          <Card
            {...{
              className,
              ...cardProps,
            }}
          >
            {renderContent()}
          </Card>
        </Grid>
      </Hidden>

      {/* FAB para abrir sidebar en móviles */}
      <Hidden {...{
        [`${breakpoint === 'sm' ? 'md' : 'lg'}Up`]: true,
      }}>
        <Fab
          {...{
            color: 'primary',
            className: `${classes.sidebarFab} ${fabProps.className || ''}`,
            onClick: handleOpen,
            style: fabStyles,
            ...fabProps,
          }}
        >
          {fabProps.icon || <HelpIcon />}
        </Fab>
      </Hidden>

      {/* Drawer sidebar para móviles */}
      <Drawer
        {...{
          anchor,
          open: sidebarOpen,
          onClose: handleClose,
          className: `${classes.drawer} ${drawerProps.className || ''}`,
          classes: {
            paper: drawerProps.paperClassName,
          },
          style: drawerStyles,
          ...drawerProps,
        }}
      >
        <Card
          {...{
            style: {
              height: '100%',
              boxShadow: 'none',
              ...cardProps.style,
            },
            ...cardProps,
          }}
        >
          {contentWithCloseButton}
        </Card>
      </Drawer>
    </>
  );
}

ResponsiveSidebar.propTypes = {
  /**
   * Contenido del sidebar. Puede ser un elemento React o una función que recibe:
   * { isMobile, isOpen, onClose }
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,

  /**
   * Props para el FAB
   */
  fabProps: PropTypes.shape({
    icon: PropTypes.node,
    color: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
  }),

  /**
   * Props para el Drawer
   */
  drawerProps: PropTypes.shape({
    anchor: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
    className: PropTypes.string,
    paperClassName: PropTypes.string,
  }),

  /**
   * Props para el Card contenedor
   */
  cardProps: PropTypes.shape({
    className: PropTypes.string,
    style: PropTypes.object,
  }),

  /**
   * Props para el Grid item en desktop
   */
  gridProps: PropTypes.shape({
    md: PropTypes.number,
    sm: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
  }),

  /**
   * Breakpoint donde cambia de desktop a móvil
   */
  breakpoint: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),

  /**
   * Posición del FAB
   */
  fabPosition: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
  }),

  /**
   * Ancho del drawer en móvil
   */
  drawerWidth: PropTypes.shape({
    mobile: PropTypes.string,
    maxWidth: PropTypes.number,
  }),

  /**
   * Si mostrar el botón de cerrar en móvil
   */
  showCloseButton: PropTypes.bool,

  /**
   * Lado desde donde se abre el drawer
   */
  anchor: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),

  /**
   * Clase CSS adicional
   */
  className: PropTypes.string,
};

ResponsiveSidebar.defaultProps = {
  fabProps: {},
  drawerProps: {},
  cardProps: {},
  gridProps: {},
  breakpoint: 'sm',
  fabPosition: {
    bottom: 16,
    right: 16,
  },
  drawerWidth: {
    mobile: '90vw',
    maxWidth: 400,
  },
  showCloseButton: true,
  anchor: 'right',
  className: '',
};

export default ResponsiveSidebar;
